import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import { useAuth } from '../../context/AuthContext';
import { roleToRedirectPath } from '../../lib/auth';
import { api } from '../../lib/api';

function getApiErrorMessage(error: unknown) {
  const e = error as any;
  return e?.response?.data?.detail || e?.message || 'Eroare. Încearcă din nou.';
}

type Section = {
  id: string;
  title: string;
  description: string;
  icon: string;
};

const HomePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { me } = useAuth();

  const [offerOpen, setOfferOpen] = useState(false);
  const [offerBusy, setOfferBusy] = useState(false);
  const [offerSuccessId, setOfferSuccessId] = useState<number | null>(null);
  const [offerError, setOfferError] = useState<string | null>(null);

  const [offerFirstName, setOfferFirstName] = useState('');
  const [offerLastName, setOfferLastName] = useState('');
  const [offerSchool, setOfferSchool] = useState('');
  const [offerPhone, setOfferPhone] = useState('');
  const [offerEmail, setOfferEmail] = useState('');
  const [offerSubject, setOfferSubject] = useState('Solicitare ofertă');
  const [offerMessage, setOfferMessage] = useState('');
  const [offerPrivacyOk, setOfferPrivacyOk] = useState(false);

  const openOffer = useCallback(() => {
    setOfferOpen(true);
    setOfferSuccessId(null);
    setOfferError(null);
  }, []);

  const submitOffer = useCallback(async () => {
    const first = offerFirstName.trim();
    const last = offerLastName.trim();
    const school = offerSchool.trim();
    const phone = offerPhone.trim();
    const email = offerEmail.trim();
    const subject = offerSubject.trim() || 'Solicitare ofertă';
    const msg = offerMessage.trim();

    if (!offerPrivacyOk) {
      setOfferError('Te rugăm să confirmi acordul pentru prelucrarea datelor.');
      return;
    }
    if (!first || !last) {
      setOfferError('Completează nume și prenume.');
      return;
    }
    if (!school) {
      setOfferError('Completează școala / instituția.');
      return;
    }
    if (!email || !email.includes('@')) {
      setOfferError('Email invalid.');
      return;
    }

    const bodyLines = [
      `Nume: ${last} ${first}`,
      `Școala / instituția: ${school}`,
      phone ? `Telefon: ${phone}` : null,
      '',
      msg ? `Mesaj:\n${msg}` : 'Mesaj: (necompletat)',
      '',
      `Trimis din: ${window.location.href}`,
    ].filter(Boolean) as string[];

    const payload = new FormData();
    payload.append('category', 'offer');
    payload.append('subject', subject);
    payload.append('message', bodyLines.join('\n'));
    payload.append('contact_email', email);

    setOfferBusy(true);
    setOfferError(null);
    try {
      const resp = await api.post('/api/contact/submit', payload);
      if (!resp.data?.ok) throw new Error(String(resp.data?.detail || 'Nu am putut trimite solicitarea.'));
      setOfferSuccessId(Number(resp.data?.id || 0) || null);
    } catch (e) {
      setOfferError(getApiErrorMessage(e));
    } finally {
      setOfferBusy(false);
    }
  }, [
    offerEmail,
    offerFirstName,
    offerLastName,
    offerMessage,
    offerPhone,
    offerPrivacyOk,
    offerSchool,
    offerSubject,
  ]);

  useEffect(() => {
    const isStandalone =
      Boolean(window.matchMedia?.('(display-mode: standalone)')?.matches) ||
      Boolean((window.navigator as any)?.standalone);
    if (isStandalone) navigate('/authentication-portal', { replace: true });
  }, [navigate]);

  const dashboardPath = useMemo(() => (me?.role ? roleToRedirectPath(me.role) : null), [me?.role]);
  const demoUrl = useMemo(() => {
    const raw = String((import.meta as any)?.env?.VITE_DEMO_PORTAL_URL || '').trim();
    if (raw) return raw;
    const host = String(window.location.hostname || '').toLowerCase();
    if (host === 'scola.ro' || host === 'www.scola.ro') return 'https://demo.scola.ro/test';
    return '/test';
  }, []);

  const sections: Section[] = [
    {
      id: 'comunicare',
      title: 'Comunicare (chat)',
      description:
        'Mesaje private și de grup, anunțuri și notificări — totul într-un singur loc, cu un istoric clar pentru elevi, părinți și profesori.',
      icon: 'MessageSquare',
    },
    {
      id: 'comunitate',
      title: 'Comunitate (rețea socială)',
      description:
        'Postări, reacții și comentarii pentru viața școlii: anunțuri, evenimente și activități care conectează elevii, profesorii și părinții.',
      icon: 'Users',
    },
    {
      id: 'catalog',
      title: 'Catalog digital',
      description:
        'Note, absențe, statistici și configurări pe structură (semestru/trimestru/module) — ușor de urmărit, ușor de gestionat.',
      icon: 'BookOpen',
    },
    {
      id: 'niveluri',
      title: 'Adaptare pe niveluri',
      description:
        'Platforma se adaptează pentru toate nivelurile: primar, gimnazial/liceal și universitar — cu structură și fluxuri potrivite fiecărei instituții.',
      icon: 'GraduationCap',
    },
    {
      id: 'teme-cursuri',
      title: 'Teme & cursuri',
      description:
        'Materiale, teme, fișiere, evaluări și feedback — organizate pe clase și materii, cu acces rapid pentru toți participanții.',
      icon: 'ClipboardCheck',
    },
    {
      id: 'control-parental',
      title: 'Control parental',
      description:
        'Transparență pentru părinți: progres, prezență și comunicare, astfel încât sprijinul pentru elev să fie constant și informat.',
      icon: 'Shield',
    },
    {
      id: 'online',
      title: 'Cursuri online (audio & video)',
      description:
        'Întâlniri și sesiuni online cu resurse atașate — pentru lecții, consultații sau pregătire suplimentară.',
      icon: 'Video',
    },
    {
      id: 'programari',
      title: 'Programări & calendar',
      description:
        'Evenimente, programări, remindere și invitări — pentru un flux clar de activități (școală, părinți, elevi).',
      icon: 'Calendar',
    },
    {
      id: 'implementare',
      title: 'Implementare flexibilă',
      description:
        'Poate fi implementată treptat: chiar și pe o singură clasă, apoi extinsă la nivel de școală, fără blocaje.',
      icon: 'Layers',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Scola - Platformă educațională</title>
        <meta
          name="description"
          content="Scola este o platformă educațională pentru comunicare, comunitate, catalog, teme, cursuri online și control parental — cu implementare flexibilă și focus pe securitatea datelor."
        />
      </Helmet>

      <div className="bg-background">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:py-12">
          <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex items-center justify-center w-[72px] h-[72px] rounded-md">
                <img src="/assets/scola-logo.png" alt="Scola" className="w-[64px] h-[64px] object-contain" />
              </div>
              <div className="leading-tight">
                <div
                  className="text-xl sm:text-2xl font-extrabold tracking-tight bg-gradient-to-r from-blue-700 via-sky-500 to-green-500 bg-clip-text text-transparent"
                  style={{ fontFamily: 'Sora, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}
                >
                  Scola
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">Platformă educațională</div>
              </div>
            </Link>

            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end">
              {dashboardPath ? (
                <Button asChild variant="outline" size="sm" iconName="LayoutDashboard" iconPosition="left">
                  <Link to={dashboardPath}>Dashboard</Link>
                </Button>
              ) : null}
              <Button variant="outline" size="sm" iconName="FileText" iconPosition="left" onClick={openOffer}>
                Solicită ofertă
              </Button>
              <Button asChild variant="outline" size="sm" iconName="FlaskConical" iconPosition="left">
                {demoUrl.startsWith('http') ? (
                  <a href={demoUrl} target="_blank" rel="noreferrer">
                    Testează
                  </a>
                ) : (
                  <Link to={demoUrl}>Testează</Link>
                )}
              </Button>
              <Button asChild variant="default" size="sm" iconName="LogIn" iconPosition="right">
                <Link to="/authentication-portal">Autentificare</Link>
              </Button>
            </div>
          </header>

          <section className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
                <Icon name="Sparkles" size={14} className="text-primary" />
                Comunicare, catalog și comunitate — într-o singură platformă
              </div>

              <h1 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
                Platformă modernă pentru școli, profesori, elevi și părinți
              </h1>
              <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-xl">
                Scola centralizează comunicarea, gestionarea academică și colaborarea. Implementare flexibilă (chiar
                și pe o singură clasă), cu accent pe securitatea datelor.
              </p>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" iconName="ArrowRight" iconPosition="right">
                  <Link to="/authentication-portal">Începe acum</Link>
                </Button>
                <Button asChild size="lg" variant="outline" iconName="FileText" iconPosition="left">
                  <Link to="/legal/abonamente" state={{ backgroundLocation: location }}>
                    Abonamente & implementare
                  </Link>
                </Button>
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-xl border border-border bg-card p-4">
                  <div className="text-sm font-semibold text-foreground">Implementare graduală</div>
                  <div className="text-xs text-muted-foreground mt-1">De la o clasă la o școală întreagă</div>
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                  <div className="text-sm font-semibold text-foreground">Flux unificat</div>
                  <div className="text-xs text-muted-foreground mt-1">Chat, catalog, teme, cursuri</div>
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                  <div className="text-sm font-semibold text-foreground">Date protejate</div>
                  <div className="text-xs text-muted-foreground mt-1">Control acces + transparență</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl blur-3xl" />
              <div className="relative rounded-2xl border border-border bg-card p-4 shadow-lg">
                <Image
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1730ad428-1764655063619.png"
                  alt="Previzualizare platformă"
                  className="w-full h-72 sm:h-80 object-cover rounded-xl"
                />
                <div className="mt-4 text-xs text-muted-foreground">
                  Placeholder imagine — aici poți pune capturi reale din platformă.
                </div>
              </div>
            </div>
          </section>

          <section className="mt-14">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-2xl font-semibold text-foreground">Ce include platforma</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Module gândite pentru utilizarea zilnică, cu o experiență coerentă pentru toate rolurile.
                </p>
              </div>
              <div className="flex gap-2">
                <Button asChild variant="ghost" size="sm" iconName="ScrollText" iconPosition="left">
                  <Link to="/legal/termeni-si-conditii" state={{ backgroundLocation: location }}>
                    Termeni
                  </Link>
                </Button>
                <Button asChild variant="ghost" size="sm" iconName="Lock" iconPosition="left">
                  <Link to="/legal/politica-de-confidentialitate" state={{ backgroundLocation: location }}>
                    Confidențialitate
                  </Link>
                </Button>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sections.map((s) => (
                <div
                  key={s.id}
                  id={s.id}
                  className="rounded-2xl border border-border bg-card p-5 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name={s.icon as any} size={20} className="text-primary" />
                    </div>
                    <div className="text-sm font-semibold text-foreground">{s.title}</div>
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground">{s.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="rounded-2xl border border-border bg-card p-6">
                <h2 className="text-2xl font-semibold text-foreground">Securitatea datelor</h2>
                <p className="mt-3 text-sm text-muted-foreground">
                  Acces pe roluri (profesor, elev, părinte, admin), acțiuni urmărite și documente legale dedicate.
                  Platforma poate fi configurată pentru nevoile fiecărei unități.
                </p>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded-xl border border-border bg-background p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                      <Icon name="KeyRound" size={16} className="text-primary" />
                      Control acces
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Permisiuni clare și fluxuri specifice fiecărui rol.
                    </div>
                  </div>
                  <div className="rounded-xl border border-border bg-background p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                      <Icon name="ShieldCheck" size={16} className="text-primary" />
                      Conformitate
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Termeni, confidențialitate, cookies, GDPR (unde este cazul).
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="grid grid-cols-2 gap-3">
                  <div className="group relative rounded-xl border border-primary/15 bg-background overflow-hidden h-28 sm:h-32 shadow-sm ring-1 ring-primary/10 transition-all hover:shadow-md hover:ring-primary/25">
                    <img
                      src="/assets/images/chat.png"
                      alt="Captură: Chat"
                      className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                    />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/55 via-black/25 to-transparent" />
                    <div className="pointer-events-none absolute left-2 bottom-2 inline-flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold text-white ring-1 ring-white/15">
                      <Icon name="MessageSquare" size={12} className="text-white/90" />
                      <span>Chat</span>
                    </div>
                  </div>
                  <div className="group relative rounded-xl border border-primary/15 bg-background overflow-hidden h-28 sm:h-32 shadow-sm ring-1 ring-primary/10 transition-all hover:shadow-md hover:ring-primary/25">
                    <img
                      src="/assets/images/catalog.png"
                      alt="Captură: Catalog"
                      className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                    />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/55 via-black/25 to-transparent" />
                    <div className="pointer-events-none absolute left-2 bottom-2 inline-flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold text-white ring-1 ring-white/15">
                      <Icon name="BookOpen" size={12} className="text-white/90" />
                      <span>Catalog</span>
                    </div>
                  </div>
                  <div className="group relative rounded-xl border border-primary/15 bg-background overflow-hidden h-28 sm:h-32 shadow-sm ring-1 ring-primary/10 transition-all hover:shadow-md hover:ring-primary/25">
                    <img
                      src="/assets/images/comunitate.png"
                      alt="Captură: Comunitate"
                      className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                    />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/55 via-black/25 to-transparent" />
                    <div className="pointer-events-none absolute left-2 bottom-2 inline-flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold text-white ring-1 ring-white/15">
                      <Icon name="Users" size={12} className="text-white/90" />
                      <span>Comunitate</span>
                    </div>
                  </div>
                  <div className="group relative rounded-xl border border-primary/15 bg-background overflow-hidden h-28 sm:h-32 shadow-sm ring-1 ring-primary/10 transition-all hover:shadow-md hover:ring-primary/25">
                    <img
                      src="/assets/images/curs-online.png"
                      alt="Captură: Curs online"
                      className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                    />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/55 via-black/25 to-transparent" />
                    <div className="pointer-events-none absolute left-2 bottom-2 inline-flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold text-white ring-1 ring-white/15">
                      <Icon name="Video" size={12} className="text-white/90" />
                      <span>Cursuri online</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mt-14">
            <div className="rounded-2xl border border-border bg-gradient-to-br from-card to-background p-6 sm:p-8">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                <div>
                  <h2 className="text-2xl font-semibold text-foreground">Vrei să o testezi pe o singură clasă?</h2>
                  <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                    Implementarea poate începe simplu: o clasă, câțiva profesori, câțiva părinți. Apoi extindem gradual,
                    cu setări și abonamente potrivite.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                  <Button asChild size="lg" fullWidth iconName="UserPlus" iconPosition="right">
                    <Link to="/authentication-portal">Creează cont profesor</Link>
                  </Button>
                  <Button asChild size="lg" variant="secondary" fullWidth iconName="FlaskConical" iconPosition="left">
                    {demoUrl.startsWith('http') ? (
                      <a href={demoUrl} target="_blank" rel="noreferrer">
                        Testează demo
                      </a>
                    ) : (
                      <Link to={demoUrl}>Testează demo</Link>
                    )}
                  </Button>
                  <Button size="lg" variant="outline" fullWidth iconName="FileText" iconPosition="left" onClick={openOffer}>
                    Solicită ofertă
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {offerOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Solicită ofertă"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOfferOpen(false);
          }}
        >
          <div className="w-full max-w-xl max-h-[90dvh] rounded-xl border border-border bg-card shadow-lg overflow-hidden flex flex-col">
            <div className="flex items-start justify-between gap-3 p-4 border-b border-border">
              <div className="flex items-start gap-2">
                <Icon name="FileText" size={18} className="text-muted-foreground mt-0.5" />
                <div>
                  <div className="text-base font-semibold text-foreground">Solicită ofertă</div>
                  <div className="text-sm text-muted-foreground">
                    Trimitem solicitarea către administratorul platformei și revenim cu o ofertă.
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="p-2 rounded-md hover:bg-muted"
                onClick={() => setOfferOpen(false)}
                aria-label="Închide"
              >
                <Icon name="X" size={18} className="text-muted-foreground" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {offerSuccessId ? (
                <div className="space-y-2 pb-[calc(env(safe-area-inset-bottom)+8px)]">
                  <div className="text-sm font-semibold text-foreground">Mulțumim!</div>
                  <div className="text-sm text-muted-foreground">
                    Solicitarea ta a fost înregistrată{offerSuccessId ? (
                      <>
                        {' '}
                        cu ID <span className="text-foreground">#{offerSuccessId}</span>
                      </>
                    ) : null}
                    . În cel mai scurt timp te contactăm.
                  </div>
                </div>
              ) : (
                <div className="space-y-3 pb-[calc(env(safe-area-inset-bottom)+8px)]">
                  {offerError ? <div className="text-sm text-destructive">{offerError}</div> : null}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="block text-sm font-medium text-foreground">
                      Nume *
                      <input
                        value={offerLastName}
                        onChange={(e) => setOfferLastName(e.target.value)}
                        className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Popescu"
                        autoComplete="family-name"
                      />
                    </label>
                    <label className="block text-sm font-medium text-foreground">
                      Prenume *
                      <input
                        value={offerFirstName}
                        onChange={(e) => setOfferFirstName(e.target.value)}
                        className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Ion"
                        autoComplete="given-name"
                      />
                    </label>
                  </div>

                  <label className="block text-sm font-medium text-foreground">
                    Școala / instituția *
                    <input
                      value={offerSchool}
                      onChange={(e) => setOfferSchool(e.target.value)}
                      className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Școala Gimnazială Nr. 1"
                      autoComplete="organization"
                    />
                  </label>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="block text-sm font-medium text-foreground">
                      Telefon
                      <input
                        value={offerPhone}
                        onChange={(e) => setOfferPhone(e.target.value)}
                        className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="07xx xxx xxx"
                        autoComplete="tel"
                      />
                    </label>
                    <label className="block text-sm font-medium text-foreground">
                      Email *
                      <input
                        value={offerEmail}
                        onChange={(e) => setOfferEmail(e.target.value)}
                        className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="nume@școala.ro"
                        autoComplete="email"
                      />
                    </label>
                  </div>

                  <label className="block text-sm font-medium text-foreground">
                    Subiect *
                    <input
                      value={offerSubject}
                      onChange={(e) => setOfferSubject(e.target.value)}
                      className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Solicitare ofertă"
                    />
                  </label>

                  <label className="block text-sm font-medium text-foreground">
                    Mesaj (opțional)
                    <textarea
                      value={offerMessage}
                      onChange={(e) => setOfferMessage(e.target.value)}
                      className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[110px]"
                      placeholder="Ex: Număr elevi/profesori, județ, termenul dorit, etc."
                    />
                  </label>

                  <label className="flex items-start gap-2 text-sm text-muted-foreground select-none">
                    <input
                      type="checkbox"
                      checked={offerPrivacyOk}
                      onChange={(e) => setOfferPrivacyOk(e.target.checked)}
                      className="mt-1"
                    />
                    <span>
                      Sunt de acord ca datele transmise să fie prelucrate pentru a fi contactat(ă) legat de solicitarea de ofertă.
                    </span>
                  </label>
                </div>
              )}
            </div>

            <div className="border-t border-border p-4 pb-[calc(env(safe-area-inset-bottom)+16px)]">
              {offerSuccessId ? (
                <div className="flex justify-end">
                  <Button variant="default" onClick={() => setOfferOpen(false)}>
                    Închide
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row justify-end gap-2">
                  <Button variant="outline" onClick={() => setOfferOpen(false)} disabled={offerBusy}>
                    Renunță
                  </Button>
                  <Button variant="default" onClick={() => void submitOffer()} loading={offerBusy} disabled={offerBusy}>
                    Trimite solicitarea
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default HomePage;
